import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} StickyAddToCartHomeRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 */

/**
 * Baugèste: a custom element that manages a sticky add-to-cart bar for the
 * homepage. Unlike the product-page version (sticky-add-to-cart.js), the
 * trigger element lives in a *different* section (e.g. the showcase
 * slideshow), identified via the `data-trigger-section-id` attribute, which
 * matches a section key/id rendered as `#shopify-section-{id}`.
 *
 * Once shown, the bar stays visible for the rest of the page - it does not
 * hide again near the footer.
 *
 * @extends {Component<StickyAddToCartHomeRefs>}
 */
class StickyAddToCartHomeComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #triggerIntersectionObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#triggerIntersectionObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * Sets up the IntersectionObserver to watch the configured trigger section
   * (e.g. the homepage showcase slideshow) visibility. The bar stays visible
   * for the rest of the page once shown (no footer hide behaviour).
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const triggerElement = this.#getTriggerElement();
    if (!triggerElement) return;

    this.#triggerIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show the sticky bar once the trigger section has been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        // Trigger section scrolled back into view - hide the sticky bar again
        this.#hideStickyBar();
      }
    });

    this.#triggerIntersectionObserver.observe(triggerElement);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Handles the add to cart button click in the sticky bar by forwarding the
   * click to the hidden real add-to-cart button, so the normal ajax cart
   * flow (cart drawer, events, etc.) runs unchanged.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 1500);
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Shows the sticky bar
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the hidden product form rendered alongside this component (same section).
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    return sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * Gets the element that should trigger the sticky bar once it scrolls out
   * of view, based on the `data-trigger-section-id` attribute.
   * @returns {HTMLElement | null}
   */
  #getTriggerElement() {
    const triggerSectionId = this.dataset.triggerSectionId;
    if (!triggerSectionId) return null;

    return document.getElementById(`shopify-section-${triggerSectionId}`);
  }
}

if (!customElements.get('sticky-add-to-cart-home')) {
  customElements.define('sticky-add-to-cart-home', StickyAddToCartHomeComponent);
}
